import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useLocalized } from "../lib/localized";
import { useCategories } from "../lib/useCategories";
import { api, imageUrl } from "../lib/api";
import useDocumentSEO from "../lib/useDocumentSEO";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import CTABand from "../components/site/CTABand";
import FadeIn from "../components/site/FadeIn";
import BackToTop from "../components/site/BackToTop";

const ALL_SLUG = "all";

export default function ProjectsIndex() {
  const { t, lang } = useLang();
  const lp = useLocalized();
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(ALL_SLUG);
  const { categories } = useCategories();

  useEffect(() => {
    Promise.all([api.get("/site-content"), api.get("/projects", { params: { status: "published" } })])
      .then(([c, p]) => {
        setContent(c.data);
        setProjects(p.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useDocumentSEO({
    title: lang === "id"
      ? "Proyek — Cipta Andalan"
      : "Projects — Cipta Andalan",
    description: lang === "id"
      ? "Katalog proyek PT Cipta Andalan Persada: residensial, komersial, industri, dan interior."
      : "Project catalogue from PT Cipta Andalan Persada: residential, commercial, industrial, and interior.",
    lang,
  });

  const filtered = useMemo(() => {
    if (active === ALL_SLUG) return projects;
    return projects.filter((p) => p.category === active);
  }, [active, projects]);

  const allLabel =
    t(content?.projects_section?.filters?.all) || (lang === "id" ? "Semua" : "All");

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#5d5f64]">
        Loading…
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Nav content={content} />
      <main>
        <section className="container-x pt-32 md:pt-40 pb-16 md:pb-20" data-testid="projects-index">
          <FadeIn>
            <div className="eyebrow mb-6">{t(content.projects_section?.eyebrow)}</div>
            <h1 className="font-display font-bold text-[#16181c] text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-[-0.02em]">
              {lang === "id" ? "Semua proyek" : "All projects"}
            </h1>
            <p className="mt-6 text-[#5d5f64] text-base md:text-[17px] max-w-[60ch]">
              {lang === "id"
                ? "Sebuah katalog pekerjaan kami — residensial, komersial, industri, dan interior."
                : "A catalogue of our work — residential, commercial, industrial, and interior."}
            </p>
          </FadeIn>
        </section>

        <section className="container-x pb-24 md:pb-32">
          <div
            className="flex md:flex-wrap gap-3 mb-12 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 md:overflow-visible scroll-smooth"
            data-testid="projects-index-filters"
          >
            {[{ slug: ALL_SLUG, name: { id: allLabel, en: allLabel } }, ...categories].map((cat) => {
              const isActive = active === cat.slug;
              return (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setActive(cat.slug)}
                  data-testid={`index-filter-chip-${cat.slug}`}
                  className={`shrink-0 whitespace-nowrap px-5 py-2 rounded-full text-[14px] font-medium border transition-colors ${
                    isActive
                      ? "bg-[#16181c] text-white border-[#16181c]"
                      : "bg-white text-[#16181c] border-[#e3e3e1] hover:bg-[#f3f3f0]"
                  }`}
                >
                  {t(cat.name)}
                </button>
              );
            })}
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16"
            data-testid="projects-index-grid"
          >
            {filtered.map((p, idx) => (
              <FadeIn key={p.id} delay={Math.min(idx, 5) * 60}>
                <Link
                  to={lp(`/proyek/${p.slug}`, `/en/projects/${p.slug}`)}
                  className="group block"
                  data-testid={`index-project-tile-${p.slug}`}
                >
                  <div className="relative aspect-[4/5] w-full bg-[#1b1c1e] overflow-hidden rounded-2xl">
                    {p.cover_image ? (
                      <img
                        src={imageUrl(p.cover_image)}
                        alt={t(p.title || p.name)}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1b1c1e] to-[#2a2c30]" />
                    )}
                    <div className="absolute top-4 left-4 text-white/80 text-xs font-medium tracking-wide">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="eyebrow text-[#5d5f64] mb-2">
                      {(() => {
                        const c = categories.find((x) => x.slug === p.category);
                        return c ? t(c.name) : p.category;
                      })()}
                    </div>
                    <div className="flex items-start justify-between gap-6">
                      <h3 className="font-display font-semibold text-[#16181c] text-xl md:text-2xl leading-tight tracking-[-0.01em]">
                        {t(p.title || p.name)}
                      </h3>
                      <div className="text-[13px] text-[#5d5f64] whitespace-nowrap pt-1">
                        {p.location} · {p.year}
                      </div>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
            {filtered.length === 0 && (
              <div className="md:col-span-3 py-20 text-center text-[#5d5f64]">
                {lang === "id" ? "Belum ada proyek pada kategori ini." : "No projects in this category yet."}
              </div>
            )}
          </div>
        </section>

        <CTABand content={content} />
      </main>
      <Footer content={content} />
    </div>
  );
}
